// ══════════════════════════════════════════════════════════════
// CHAT PRIVADO ENTRE USUARIOS (agregado 15/10/2026)
//
// Separado de firebase-config.ts, igual que firebase-xp-import.ts.
// Funciona 100% desde el cliente, sin Firebase Functions, para
// mantener el proyecto en el plan gratuito (Spark).
//
// Colecciones nuevas:
//   PerfilesPublicos/{uid}      → nombre, avatar y nivel visibles para
//                                 otros alumnos (el chat NUNCA lee
//                                 Usuarios de otra persona).
//   Chats/{uidA_uidB}           → una conversación entre dos personas.
//                                 El id son los dos UIDs ordenados, así
//                                 nunca se duplica.
//   Chats/{id}/Mensajes/{msgId} → los mensajes.
//   ReportesChat/{id}           → mensajes reportados (solo admin los lee).
//
// La seguridad la ponen las reglas: ver claude/firestore.rules.chat.txt.
// ══════════════════════════════════════════════════════════════

import {
  collection,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
  DocumentData,
  DocumentSnapshot,
  QueryDocumentSnapshot,
} from "firebase/firestore";
import { db, Task, UserSkillRecord } from "./firebase-config";
import { getLevelFromExp } from "./utils/levels";
import { normalizeText, XP_AREAS } from "./utils/xpImport";

export const MAX_MESSAGE_LENGTH = 1000;
export const MESSAGES_PAGE_SIZE = 50;
const PREVIEW_LENGTH = 120;

// ---------- Tipos ----------

/** Las tres áreas del Desafío (mismos nombres que usa el importador de XP). */
export const SKILL_AREAS = ["Programación", "Arte", "Diseño"] as const;
export type SkillArea = (typeof SKILL_AREAS)[number];

export interface PublicProfile {
  id: string;
  Nombre: string;
  PortraitImg: string;
  Nivel: number;
  /** "" si todavía no tiene XP en ninguna de las tres áreas. */
  MejorSkill: SkillArea | "";
}

/**
 * Área con más XP del usuario. Reconoce nombres viejos de skill
 * ("programming", "art", "design"...) con los alias de XP_AREAS.
 * Skills que no son de las tres áreas no cuentan.
 */
export const getBestSkillArea = (skills: UserSkillRecord[] = []): SkillArea | "" => {
  const totals: Record<string, number> = {};
  skills.forEach((skill) => {
    const name = normalizeText(skill.Materia);
    const area = XP_AREAS.find((a) =>
      [a.skillLabel, ...a.skillAliases].map(normalizeText).includes(name),
    );
    if (area) totals[area.skillLabel] = (totals[area.skillLabel] ?? 0) + (skill.Progress ?? 0);
  });
  let best: SkillArea | "" = "";
  let bestValue = 0;
  SKILL_AREAS.forEach((area) => {
    if ((totals[area] ?? 0) > bestValue) {
      best = area;
      bestValue = totals[area];
    }
  });
  return best;
};

export interface ChatSummary {
  id: string;
  Miembros: string[];
  UltimoMensaje: string;
  UltimoAutorId: string;
  ActualizadoEl: Date | null;
  LeidoPor: Record<string, Date | null>;
}

export interface ChatMessage {
  id: string;
  AutorId: string;
  Texto: string;
  Fecha: Date | null;
}

// ---------- Helpers ----------

const toDate = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const v = value as { toDate?: () => Date; seconds?: number };
  if (typeof v.toDate === "function") return v.toDate();
  if (typeof v.seconds === "number") return new Date(v.seconds * 1000);
  return null;
};

/** Id de la conversación entre dos usuarios: UIDs ordenados unidos por "_". */
export const getChatId = (uidA: string, uidB: string) =>
  [uidA, uidB].sort().join("_");

/** El UID de la otra persona de un chat. */
export const getOtherMemberId = (chat: ChatSummary, myUid: string) =>
  chat.Miembros.find((id) => id !== myUid) ?? "";

/** true si el último mensaje es de la otra persona y no lo vi todavía. */
export const isChatUnread = (chat: ChatSummary, myUid: string) => {
  if (!chat.UltimoMensaje || chat.UltimoAutorId === myUid) return false;
  const lastRead = chat.LeidoPor[myUid];
  if (!lastRead) return true;
  return Boolean(chat.ActualizadoEl && chat.ActualizadoEl > lastRead);
};

// "estimate" hace que los serverTimestamp pendientes (mensaje recién
// enviado, todavía sin confirmar) tengan una fecha local en vez de null.
const SNAPSHOT_OPTIONS = { serverTimestamps: "estimate" as const };

export const normalizeChat = (
  snap: QueryDocumentSnapshot<DocumentData> | DocumentSnapshot<DocumentData>,
): ChatSummary => {
  const d = snap.data(SNAPSHOT_OPTIONS) ?? {};
  const leidoRaw = (d.LeidoPor ?? {}) as Record<string, unknown>;
  const LeidoPor: Record<string, Date | null> = {};
  Object.keys(leidoRaw).forEach((uid) => {
    LeidoPor[uid] = toDate(leidoRaw[uid]);
  });
  return {
    id: snap.id,
    Miembros: Array.isArray(d.Miembros) ? d.Miembros : [],
    UltimoMensaje: typeof d.UltimoMensaje === "string" ? d.UltimoMensaje : "",
    UltimoAutorId: typeof d.UltimoAutorId === "string" ? d.UltimoAutorId : "",
    ActualizadoEl: toDate(d.ActualizadoEl),
    LeidoPor,
  };
};

export const normalizeMessage = (
  snap: QueryDocumentSnapshot<DocumentData>,
): ChatMessage => {
  const d = snap.data(SNAPSHOT_OPTIONS);
  return {
    id: snap.id,
    AutorId: typeof d.AutorId === "string" ? d.AutorId : "",
    Texto: typeof d.Texto === "string" ? d.Texto : "",
    Fecha: toDate(d.Fecha),
  };
};

const normalizeProfile = (
  snap: QueryDocumentSnapshot<DocumentData>,
): PublicProfile => {
  const d = snap.data();
  return {
    id: snap.id,
    Nombre: typeof d.Nombre === "string" && d.Nombre ? d.Nombre : "Alumno",
    PortraitImg: typeof d.PortraitImg === "string" ? d.PortraitImg : "",
    Nivel: typeof d.Nivel === "number" ? d.Nivel : 1,
    MejorSkill: SKILL_AREAS.includes(d.MejorSkill) ? d.MejorSkill : "",
  };
};

// ---------- Perfiles públicos ----------

export async function GetPublicProfiles(): Promise<PublicProfile[]> {
  const snap = await getDocs(collection(db, "PerfilesPublicos"));
  return snap.docs
    .map(normalizeProfile)
    .sort((a, b) => a.Nombre.localeCompare(b.Nombre));
}

const buildPublicProfile = (user: Task) => ({
  Nombre: user.Nombre || "Alumno",
  PortraitImg: user.PortraitImg || "",
  Nivel: getLevelFromExp(user.CurrentExp ?? 0),
  MejorSkill: getBestSkillArea(user.Skill ?? []),
  ActualizadoEl: serverTimestamp(),
});

/** El propio usuario publica su perfil (lo llama usePublicProfileSync). */
export async function UpsertMyPublicProfile(user: Task) {
  await setDoc(doc(db, "PerfilesPublicos", user.id), buildPublicProfile(user));
}

/**
 * Admin: crea/actualiza el perfil público de TODOS los usuarios, para
 * que aparezcan en el chat aunque nunca hayan entrado a la web.
 */
export async function SyncAllPublicProfiles(users: Task[]): Promise<number> {
  const eligible = users.filter((u) => u.id && u.Rol !== "admin");
  for (let start = 0; start < eligible.length; start += 400) {
    const batch = writeBatch(db);
    eligible.slice(start, start + 400).forEach((u) => {
      batch.set(doc(db, "PerfilesPublicos", u.id), buildPublicProfile(u));
    });
    await batch.commit();
  }
  return eligible.length;
}

// ---------- Mensajes ----------

export interface SendMessageParams {
  myUid: string;
  otherUid: string;
  text: string;
  /** true si el documento del chat ya existe (viene de useMyChats). */
  chatExists: boolean;
}

/**
 * Envía un mensaje. El documento del chat se crea recién con el primer
 * mensaje, así no quedan conversaciones vacías en la lista.
 * Mensaje + resumen del chat se escriben juntos (todo o nada).
 */
export async function SendChatMessage({
  myUid,
  otherUid,
  text,
  chatExists,
}: SendMessageParams) {
  const texto = text.trim();
  if (!texto) return;
  if (texto.length > MAX_MESSAGE_LENGTH) {
    throw new Error(`El mensaje supera los ${MAX_MESSAGE_LENGTH} caracteres.`);
  }

  const chatId = getChatId(myUid, otherUid);
  const chatRef = doc(db, "Chats", chatId);
  const preview =
    texto.length > PREVIEW_LENGTH ? `${texto.slice(0, PREVIEW_LENGTH)}…` : texto;

  const commit = (create: boolean) => {
    const batch = writeBatch(db);
    if (create) {
      batch.set(chatRef, {
        Miembros: [myUid, otherUid].sort(),
        UltimoMensaje: preview,
        UltimoAutorId: myUid,
        ActualizadoEl: serverTimestamp(),
        LeidoPor: { [myUid]: serverTimestamp() },
      });
    } else {
      batch.update(chatRef, {
        UltimoMensaje: preview,
        UltimoAutorId: myUid,
        ActualizadoEl: serverTimestamp(),
        [`LeidoPor.${myUid}`]: serverTimestamp(),
      });
    }
    batch.set(doc(collection(chatRef, "Mensajes")), {
      AutorId: myUid,
      Texto: texto,
      Fecha: serverTimestamp(),
    });
    return batch.commit();
  };

  try {
    await commit(!chatExists);
  } catch (error) {
    // Caso raro: la otra persona creó el chat al mismo tiempo. Reintentamos
    // como actualización una sola vez.
    if (chatExists) throw error;
    await commit(false);
  }
}

/** Marca el chat como leído por mí (solo toca mi clave de LeidoPor). */
export async function MarkChatRead(chatId: string, myUid: string) {
  await updateDoc(doc(db, "Chats", chatId), {
    [`LeidoPor.${myUid}`]: serverTimestamp(),
  });
}

// ---------- Reportes ----------

export async function ReportChatMessage(params: {
  chatId: string;
  message: ChatMessage;
  myUid: string;
  motivo?: string;
}) {
  await setDoc(doc(collection(db, "ReportesChat")), {
    ChatId: params.chatId,
    MensajeId: params.message.id,
    AutorId: params.message.AutorId,
    Texto: params.message.Texto,
    ReportadoPor: params.myUid,
    Motivo: params.motivo ?? "",
    Fecha: serverTimestamp(),
  });
}
